const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const revision = /^[0-9a-f]{64}$/;
const target = /^top-[1-9]\d*-(DEPOSIT|BET|WIN)$/;
const count = value => Number.isSafeInteger(value) && value >= 0;
const money = value => typeof value === 'string' && /^\d+\.\d{2}$/.test(value);
const score = value => typeof value === 'string' && /^\d+\.\d{4}$/.test(value);
const flow = value => typeof value === 'string' && /^\d+(\.\d+)?$/.test(value);
const privateKeys = new Set(['memberId', 'memberNo', 'rules', 'scores', 'walletTransactionId', 'resultId', 'lastAt']);
function publicOnly(value) {
  return !value || typeof value !== 'object' || Object.entries(value).every(([key, child]) => !privateKeys.has(key) && publicOnly(child));
}

// Validate the authoritative public projection before enabling any claim.
export function validEuroTopState(state, activityId) {
  return state && String(state.activityId) === String(activityId) && typeof state.enabled === 'boolean' && count(state.serverNow) && money(state.walletBalance) && publicOnly(state) &&
    Array.isArray(state.confirmedRequestIds) && state.confirmedRequestIds.every(id => uuid.test(id)) && Array.isArray(state.boards) && new Set(state.boards.map(row => row.key)).size === state.boards.length &&
    state.boards.every(row => target.test(row.key) && ['DEPOSIT', 'BET', 'WIN'].includes(row.kind) && revision.test(row.revision) && typeof row.title === 'string' && typeof row.name === 'string' &&
      ['ended', 'final', 'canClaim', 'claimed'].every(key => typeof row[key] === 'boolean') && count(row.ownRank) && count(row.recordId) && money(row.amount) && score(row.ownScore) && flow(row.flow) &&
      (!row.claimed || row.final && row.recordId > 0 && !row.canClaim) && (!row.canClaim || row.ended && row.ownRank > 0 && Number(row.amount) > 0 && !row.claimed) &&
      Array.isArray(row.entries) && row.entries.every(entry => count(entry.rank) && entry.rank > 0 && typeof entry.account === 'string' && score(entry.score) && money(entry.amount) && flow(entry.flow) && typeof entry.self === 'boolean'));
}

// Keep the original UUID and rules revision through refresh, lost responses, login and device changes.
export function createEuroTopSession({ api, identity, token, storage, newRequestId, changed = () => {} }) {
  let activityId, actor, openedToken, alive = true, version = 0, readVersion = 0;
  let value = { state: null, loading: false, busy: false, error: '', pending: null };
  const publish = patch => { value = { ...value, ...patch }; changed(value); };
  const storageKey = () => `euro-top:${actor}:${activityId}`;
  const current = sequence => alive && sequence === version && actor === identity() && openedToken === token();
  const readPending = () => {
    const saved = storage.get(storageKey());
    if (!saved) return null;
    if (!uuid.test(saved.requestId) || !target.test(saved.targetKey) || !revision.test(saved.revision) || Object.keys(saved).some(key => !['targetKey', 'revision', 'requestId'].includes(key))) throw new Error('上次排行领取请求待核对，请保留原记录');
    return saved;
  };
  function accept(state) {
    if (!validEuroTopState(state, activityId)) throw new Error('排行状态暂不可用，请重新查询');
    let pending = readPending();
    const board = pending && state.boards.find(row => row.key === pending.targetKey);
    if (pending && (state.confirmedRequestIds.includes(pending.requestId) || board?.claimed)) { storage.remove(storageKey()); pending = null; }
    publish({ state, pending, error: '' });
  }
  async function refresh() {
    if (!alive || !actor || !openedToken || value.busy) return;
    const sequence = version, request = ++readVersion;
    publish({ loading: true, error: '' });
    try { const state = await api.state(activityId); if (current(sequence) && request === readVersion) accept(state); }
    catch (error) { if (current(sequence) && request === readVersion) publish({ error: error.message || '排行读取失败' }); }
    finally { if (current(sequence) && request === readVersion) publish({ loading: false }); }
  }
  async function open(id) {
    version++; readVersion++; alive = true; activityId = id; actor = identity(); openedToken = token();
    value = { state: null, loading: false, busy: false, error: '', pending: null }; publish({});
    if (!actor || !openedToken) { publish({ error: '登录后查看排行与领取结果' }); return; }
    try { publish({ pending: readPending() }); } catch (error) { publish({ error: error.message }); return; }
    await refresh();
  }
  async function send(data) {
    if (!current(version) || value.busy || value.loading || !value.state) return;
    const sequence = version;
    try {
      const saved = readPending();
      if (saved && JSON.stringify(saved) !== JSON.stringify(data)) throw new Error('请先核对上一次排行领取');
      storage.set(storageKey(), data); publish({ busy: true, pending: data, error: '' });
      const state = await api.claim(activityId, data);
      if (current(sequence)) accept(state);
    } catch (error) {
      if (current(sequence)) {
        // Only an explicit server proof after the original UUID lookup can end a rejected request.
        if (error.errorCode === 'EURO_TOP_NOT_COMMITTED' && readPending()?.requestId === data.requestId) {
          storage.remove(storageKey()); publish({ pending: null });
        }
        publish({ error: error.message || '领取结果待确认，请先查询原结果' });
      }
    }
    finally { if (current(sequence)) publish({ busy: false }); }
  }
  async function claim(key) {
    if (!current(version) || value.pending || value.error || value.busy || value.loading) return;
    const board = value.state?.boards.find(row => row.key === key);
    if (!board?.canClaim) return;
    const requestId = newRequestId(); if (!uuid.test(requestId)) throw new Error('当前浏览器无法生成请求编号');
    return send({ targetKey: key, revision: board.revision, requestId });
  }
  return { open, refresh, claim, retry: () => value.pending && send(value.pending), dispose() { alive = false; version++; readVersion++; } };
}
